import React, { useRef, useState, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Crop } from "lucide-react";

interface PannableImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  style?: React.CSSProperties;
  /** When provided, shows a "Save crop" button that produces a cropped data URL of the currently visible area. */
  onCommitCrop?: (croppedDataUrl: string) => void;
}

export function PannableImage({
  src,
  alt,
  className,
  containerClassName,
  style,
  onCommitCrop,
}: PannableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canPan, setCanPan] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  const checkIfPannable = useCallback(() => {
    const container = containerRef.current;
    if (!container || naturalSize.w === 0 || naturalSize.h === 0) return;
    const containerRect = container.getBoundingClientRect();
    const containerW = containerRect.width;
    const containerH = containerRect.height;
    const scale = Math.max(containerW / naturalSize.w, containerH / naturalSize.h);
    const scaledW = naturalSize.w * scale;
    const scaledH = naturalSize.h * scale;
    const pannable = scaledW > containerW + 1 || scaledH > containerH + 1;
    setCanPan(pannable);
    if (!pannable) {
      setPosition({ x: 0, y: 0 });
    }
  }, [naturalSize]);

  useEffect(() => {
    checkIfPannable();
  }, [checkIfPannable]);

  useEffect(() => {
    const handleResize = () => checkIfPannable();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [checkIfPannable]);

  const clampPosition = useCallback(
    (nextX: number, nextY: number) => {
      const container = containerRef.current;
      if (!container || naturalSize.w === 0 || naturalSize.h === 0) return { x: nextX, y: nextY };
      const containerRect = container.getBoundingClientRect();
      const containerW = containerRect.width;
      const containerH = containerRect.height;
      const scale = Math.max(containerW / naturalSize.w, containerH / naturalSize.h);
      const scaledW = naturalSize.w * scale;
      const scaledH = naturalSize.h * scale;
      const maxX = Math.max(0, (scaledW - containerW) / 2);
      const maxY = Math.max(0, (scaledH - containerH) / 2);
      return {
        x: Math.max(-maxX, Math.min(maxX, nextX)),
        y: Math.max(-maxY, Math.min(maxY, nextY)),
      };
    },
    [naturalSize]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!canPan) return;
      e.preventDefault();
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        posX: position.x,
        posY: position.y,
      };
    },
    [canPan, position]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      const next = clampPosition(dragStart.current.posX + dx, dragStart.current.posY + dy);
      setPosition(next);
    },
    [isDragging, clampPosition]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch support
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!canPan) return;
      const touch = e.touches[0];
      dragStart.current = {
        x: touch.clientX,
        y: touch.clientY,
        posX: position.x,
        posY: position.y,
      };
      setIsDragging(true);
    },
    [canPan, position]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      const dx = touch.clientX - dragStart.current.x;
      const dy = touch.clientY - dragStart.current.y;
      const next = clampPosition(dragStart.current.posX + dx, dragStart.current.posY + dy);
      setPosition(next);
    },
    [isDragging, clampPosition]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    setImageLoaded(true);
  };

  const handleCommitCrop = useCallback(() => {
    const container = containerRef.current;
    if (!container || !onCommitCrop || naturalSize.w === 0 || naturalSize.h === 0) return;
    const rect = container.getBoundingClientRect();
    const containerW = rect.width;
    const containerH = rect.height;
    const scale = Math.max(containerW / naturalSize.w, containerH / naturalSize.h);
    const scaledW = naturalSize.w * scale;
    const scaledH = naturalSize.h * scale;
    // Top-left of the scaled image relative to container, accounting for centering + pan
    const offsetX = (containerW - scaledW) / 2 + position.x;
    const offsetY = (containerH - scaledH) / 2 + position.y;
    // Container viewport in source pixels
    const sx = Math.max(0, -offsetX / scale);
    const sy = Math.max(0, -offsetY / scale);
    const sw = Math.min(naturalSize.w - sx, containerW / scale);
    const sh = Math.min(naturalSize.h - sy, containerH / scale);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      // Preserve resolution at source scale
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      try {
        const dataUrl = canvas.toDataURL("image/png");
        onCommitCrop(dataUrl);
      } catch (err) {
        console.error("Crop failed (likely CORS)", err);
      }
    };
    img.onerror = () => console.error("Crop: failed to load source image");
    img.src = src;
  }, [onCommitCrop, naturalSize, position, src]);

  // Compute the cover scale to size the inner wrapper
  const containerW = containerRef.current?.getBoundingClientRect().width ?? 0;
  const containerH = containerRef.current?.getBoundingClientRect().height ?? 0;
  const scale =
    naturalSize.w > 0 && naturalSize.h > 0
      ? Math.max(containerW / naturalSize.w, containerH / naturalSize.h)
      : 1;

  return (
    <div
      ref={containerRef}
      className={cn(
        "overflow-hidden relative select-none group",
        canPan && (isDragging ? "cursor-grabbing" : "cursor-grab"),
        containerClassName
      )}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="absolute left-1/2 top-1/2 flex items-center justify-center"
        style={{
          width: naturalSize.w * scale,
          height: naturalSize.h * scale,
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
        }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className={cn(
            "max-w-none max-h-none select-none",
            !imageLoaded && "opacity-0",
            className
          )}
          style={{
            width: naturalSize.w * scale,
            height: naturalSize.h * scale,
            ...style,
          }}
          onLoad={handleImageLoad}
        />
      </div>
      {canPan && (
        <div className={cn(
          "absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded backdrop-blur-sm pointer-events-none transition-opacity duration-300",
          isDragging ? "opacity-0" : "opacity-0 group-hover:opacity-100"
        )}>
          Drag to pan
        </div>
      )}
      {canPan && onCommitCrop && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="absolute top-2 right-2 h-7 gap-1 text-[11px] opacity-80 hover:opacity-100"
          onClick={(e) => { e.stopPropagation(); handleCommitCrop(); }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Crop className="w-3 h-3" /> Save crop
        </Button>
      )}
    </div>
  );
}

